<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import YongsinGG from './YongsinGG.vue';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import {
  parseKeywords,
  insertLineBreak,
  transformMarkedText,
  splitBySentence,
} from '@/composables/splitSentence';

const props = defineProps({
  yData: Object,
});
const { transdata, birthData, birthJijanggan, curDaeun } = storeToRefs(useAnalysisDataStore());

const ManseBirthMainArea = ref(null);
const langMode = ref('CHNKOR');
const birthTitle = ref('시일월년');
const ggNmae = computed(() => props.yData?.분석상세?.격국분석?.격국이름);
const ggType = computed(() => props.yData?.분석상세?.격국분석?.격국유형);
const ggPosition = computed(() => props.yData?.분석상세?.격국분석?.격국위치);

const ggKeywords = computed(() => {
  const raw = props.yData?.분석상세?.격국설명?.기준정보?.특징요약;
  return raw ? parseKeywords(raw).map(insertLineBreak) : [];
});

const ggDefinition = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.격국설명?.개념특징?.개념 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});

const ggPatternTitle = computed(() => props.yData?.분석상세?.격국패턴?.기준정보?.정보제목);
const ggPatternDesc = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.격국패턴?.개념특징?.개념 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const ggPatternAttribute = computed(() => {
  const sentences = splitBySentence(props.yData?.분석상세?.격국패턴?.개념특징?.특징 || '');
  return sentences.map((sentence) => transformMarkedText(sentence));
});
const ggPatternStKeywords = computed(() => {
  const raw = props.yData?.분석상세?.격국패턴?.개념특징?.강점요약;
  return raw ? parseKeywords(raw) : [];
});
const ggPatternWeaKeywords = computed(() => {
  const raw = props.yData?.분석상세?.격국패턴?.개념특징?.약점요약;
  return raw ? parseKeywords(raw) : [];
});
</script>

<template>
  <VRow class="full-height">
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">2</span>
          <span class="detail-tit__txt yongsin">격국분석</span>
        </div>
        <div class="personality-wrapper with-tit content_info_area_box">
          <div class="person-content-box">
            <div class="ys-title-box">
              <div class="ys-title-box__title">{{ ggNmae }}</div>
              <div class="ys-title-box__desc">{{ ggType }}</div>
            </div>
          </div>
          <div class="yongsin__gg-box data-sec-01-box">
            <YongsinGG
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-position="ggPosition"
            />
          </div>
          <div class="ys-circle-line-box">
            <div class="guide__circle-box">
              <div class="circle-box__overlap-group">
                <div class="yongsin-circle clear">
                  <span class="circle__main-txt" v-html="ggKeywords[0]" />
                </div>
                <div class="yongsin-circle light overlap-circle">
                  <span class="circle__main-txt" v-html="ggKeywords[1]" />
                </div>
                <div class="yongsin-circle clear overlap-circle">
                  <span class="circle__main-txt" v-html="ggKeywords[2]" />
                </div>
              </div>
            </div>
          </div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">개념</div>
            <div v-for="(html, idx) in ggDefinition" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="detail-card__top-title yongsin content_chapter_area_title">
          <span class="detail-tit__num yongsin">3</span>
          <span class="detail-tit__txt yongsin">격국패턴</span>
        </div>

        <div class="personality-wrapper content_info_area_box">
          <div class="person-content-box">
            <div class="ys-title-box">
              <div class="ys-title-box__title">{{ ggPatternTitle }}</div>
            </div>
          </div>
          <div class="yongsin-desc-box">
            <div v-for="(html, idx) in ggPatternDesc" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="yg-line-01 mt-none"></div>
          <div class="yongsin-desc-box">
            <div class="yongsin-desc__tit">특징</div>
            <div v-for="(html, idx) in ggPatternAttribute" :key="idx">
              <span class="yongsin-desc__txt" v-html="html"></span>
            </div>
          </div>
          <div class="ys-word-wrap">
            <div class="ys-word-box st-blue">
              <div class="tit-box">강점</div>
              <div class="word-box">
                <span v-for="(word, idx) in ggPatternStKeywords" :key="idx" class="word-item">
                  {{ word }}
                </span>
              </div>
            </div>
            <div class="ys-word-box st-pink">
              <div class="tit-box">보완</div>
              <div class="word-box">
                <span v-for="(word, idx) in ggPatternWeaKeywords" :key="idx" class="word-item">
                  {{ word }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>
<style scoped lang="scss">
@import '@/assets/nangman_style/_util.scss';
@import '@/assets/nangman_style/youngsin.scss';
.ys-circle-line-box {
  display: flex;
  padding: 20px 0;
  justify-content: center;
  align-items: center;
  gap: 20px;
  align-self: stretch;
  border-top: 1px solid var(--Gray-300, #ebebeb);
  border-bottom: 1px solid var(--Gray-300, #ebebeb);
}
// .personality-circle.st-ys-sub-02 {
//   border: 1px solid rgba(78, 128, 214, 0.5);
//   background: var(--gray-100-white, #fff);
//   .circle__main-txt {
//     color: #4e80d6;
//   }
//   &.bg-blue {
//     border-radius: 200px;
//     border: 1px solid rgba(78, 128, 214, 0.5);
//     background: rgba(78, 128, 214, 0.05);
//   }
//   &.bg-transparent {
//     background: transparent;
//   }
// }
</style>
